import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isLoggedIn, isAdmin, isFinance } from './services/auth';
import { NotificationProvider } from './context/NotificationContext';

import LoginPage from './pages/login/LoginPage';
import ForgotPasswordPage from './pages/login/ForgotPasswordPage';
import ResetPasswordPage from './pages/login/ResetPasswordPage';
import Layout from './components/Layout';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import CustomerFormPage from './pages/CustomerFormPage';
import JobsPage from './pages/JobsPage';
import JobFormPage from './pages/JobFormPage';
import WarrantyPage from './pages/WarrantyPage';
import WarrantyFormPage from './pages/WarrantyFormPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserFormPage from './pages/AdminUserFormPage';
import AdminConfigPage from './pages/AdminConfigPage';
import AdminPerformancePage from './pages/AdminPerformancePage';
import TrackPage from './pages/TrackPage';
import GenerateInvoicePage from './pages/finance/GenerateInvoicePage';
import RecordPaymentPage from './pages/finance/RecordPaymentPage';
import PaymentHistoryPage from './pages/finance/PaymentHistoryPage';
import InvoicesPage from './pages/finance/InvoicesPage';
import InvoiceViewPage from './pages/finance/InvoiceViewPage';
import PaymentReceiptPage from './pages/finance/PaymentReceiptPage';
import PointsHistoryPage from './pages/finance/PointsHistoryPage';
import MyProfilePage from './pages/MyProfilePage';
import MarketingSendPage from './pages/marketing/MarketingSendPage';
import MarketingTemplatesPage from './pages/marketing/MarketingTemplatesPage';
import CustomerLoginPage from './pages/customer/CustomerLoginPage';
import CustomerDashboardPage from './pages/customer/CustomerDashboardPage';
import HelpDeskPage from './pages/HelpDeskPage';

function RequireAuth({ children }) {
  return isLoggedIn() ? children : <Navigate to="/login" replace />;
}

function RequireAdmin({ children }) {
  return isAdmin() ? children : <Navigate to="/dashboard" replace />;
}

function RequireFinance({ children }) {
  return isFinance() ? children : <Navigate to="/dashboard" replace />;
}

// PUBLIC_URL is set at build time via the build:v2 / build:v3 scripts.
// e.g. PUBLIC_URL=/v2 → basename="/v2" → all routes become /v2/dashboard etc.
const BASE = process.env.PUBLIC_URL || '';

export default function App() {
  return (
    <BrowserRouter basename={BASE}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/track" element={<TrackPage />} />
        <Route path="/customer/login" element={<CustomerLoginPage />} />
        <Route path="/customer/dashboard" element={<CustomerDashboardPage />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <NotificationProvider>
                <Layout />
              </NotificationProvider>
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="profile" element={<MyProfilePage />} />

          {/* Customers */}
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/new" element={<CustomerFormPage />} />
          <Route path="customers/:uid/edit" element={<CustomerFormPage />} />

          {/* Jobs */}
          <Route path="jobs" element={<JobsPage />} />
          <Route path="jobs/new" element={<JobFormPage />} />
          <Route path="jobs/:jobcode/edit" element={<JobFormPage />} />

          {/* Warranty */}
          <Route path="warranty" element={<WarrantyPage />} />
          <Route path="warranty/new" element={<WarrantyFormPage />} />
          <Route path="warranty/:jobcode/edit" element={<WarrantyFormPage />} />

          <Route path="helpdesk" element={<HelpDeskPage />} />
          <Route path="marketing/send" element={<MarketingSendPage />} />
          <Route path="marketing/templates" element={<MarketingTemplatesPage />} />

          {/* Admin — only for admins */}
          <Route
            path="admin/users"
            element={
              <RequireAdmin>
                <AdminUsersPage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/users/new"
            element={
              <RequireAdmin>
                <AdminUserFormPage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/users/:id/edit"
            element={
              <RequireAdmin>
                <AdminUserFormPage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/config"
            element={
              <RequireAdmin>
                <AdminConfigPage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/performance"
            element={
              <RequireAdmin>
                <AdminPerformancePage />
              </RequireAdmin>
            }
          />

          <Route
            path="finance/invoice/new"
            element={<RequireFinance><GenerateInvoicePage /></RequireFinance>}
          />
          <Route
            path="finance/payment/new"
            element={<RequireFinance><RecordPaymentPage /></RequireFinance>}
          />
          <Route
            path="finance/invoices"
            element={<RequireFinance><InvoicesPage /></RequireFinance>}
          />
          <Route
            path="finance/invoices/:id"
            element={<RequireFinance><InvoiceViewPage /></RequireFinance>}
          />
          <Route
            path="finance/payments"
            element={<RequireFinance><PaymentHistoryPage /></RequireFinance>}
          />
          <Route
            path="finance/payments/:id/receipt"
            element={<RequireFinance><PaymentReceiptPage /></RequireFinance>}
          />
          <Route
            path="finance/points-history"
            element={<RequireFinance><PointsHistoryPage /></RequireFinance>}
          />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
